import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import TextField from 'components/TextField/TextField';
import { NAME, SOURCE } from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { sourceLinkCreateSchema } from 'utils/validators';

import type { TSourceLinkCreateSchema } from 'utils/validators';
import type ICustomSourceModal from './interfaces/ICustomSourceModal';

import styles from './scss/CustomSourceModal.module.scss';

function CustomSourceModal({
  openModal, title, onCancel, onAdd, titleField, titleSecondField,
}: ICustomSourceModal) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
  } = useForm<TSourceLinkCreateSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(sourceLinkCreateSchema),
    defaultValues: {
      [NAME]: '',
      [SOURCE]: '',
    },
  });

  const onHandleSubmit = handleSubmit((data) => {
    if (data[NAME] && data[SOURCE]) {
      onAdd({
        value: data[SOURCE],
        label: data[NAME],
      });
    }
    reset();
  });

  return (
    <Modal
      open={openModal}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={NAME}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <TextField
                name={name}
                value={value}
                label={titleField || t('materialityAssessment.fields.sectorName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeSector')}
              />
            )}
          />
          <Controller
            control={control}
            name={SOURCE}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <TextField
                name={name}
                value={value}
                label={titleSecondField || t('materialityAssessment.fields.sectorLink')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeLink')}
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onHandleSubmit}
            className={styles.button}
            disabled={watch(NAME) === '' || watch(SOURCE) === ''}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CustomSourceModal;
